import React, { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompanyDetailsApi, getStatesData, updateCompanyApi } from '_services';
import { CityDropdownForTrip, CommonInput, CustomSelect } from 'components';
import Tab from 'components/common/Tab'
import ImageCropModal from 'components/ImageModify/ImageCropModal';
import { constant } from 'constants/constant';
import { UserContext } from 'context';
import { companyImageUpload } from 'features';
import { errorSchema, utils } from 'helper';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { Checkbox } from 'pages/User/AddUser';
import toast from 'react-hot-toast';
import { LuUpload } from "react-icons/lu";
import { useDispatch } from 'react-redux';
import StorageService from 'utils/StorageServices';
import CompanySetting from './CompanySetting';
import InvoiceFormat from './InvoiceFormat';
import { getCityLocationList } from '_services/accountsServices/cityLocationService';

const useQuery = () => new URLSearchParams(useLocation().search);

const initialForm = {
    company_id: StorageService.getCompanyId(),
    company: "",
    contact_no: "",
    alt_contact_no: "",
    email_id: "",
    website: "",
    entity_type: '',
    gst_no: "",
    gst_state: {},
    pan_no: "",
    fssai_licence: "",
    address_line1: "",
    address_line2: "",
    state: {},
    city: {},
    pincode: "",
    company_type: []
};

const CompanyProfile = () => {
    const dispatch = useDispatch();
    useDocumentTitle("Company | T360");

    const query = useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    const tbn = query.get('tbn') || 1;

    const { masterData, setMasterData, cities, setCities } = useContext(UserContext);

    const [activeTab, setActiveTab] = useState(Number(tbn));
    const [companyDetails, setCompanyDetails] = useState('');
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState("");
    const [entityList, setEntityList] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEqal, setIsEqal] = useState(false);
    const [croppedImage, setCroppedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);

    const companyRef = useRef(null);

    useEffect(() => {
        if (cities) {
            setFilteredCities(cities.map((d) => ({
                ...d,
                label: `${d?.name}, ${d?.stateCode}`,
                value: d?.name,
            })));
        } else {
            setCities(JSON.parse(StorageService.getCities()));
        }
        if (masterData === null) {
            setMasterData(JSON.parse(StorageService.getLocalItem("master_data")));
        }
    }, [cities, masterData]);

    useEffect(() => {
        if (masterData) {
            setEntityList(masterData?.entity_type_list?.map((i) => ({
                label: i?.name,
                value: i?.id,
            })));
        }
    }, [masterData]);

    useEffect(() => {
        setIsEqal(JSON.stringify(companyRef.current) === JSON.stringify(form));
    }, [form]);

    useEffect(() => {
        getCompanyDetailsHandle();
    }, []);

    useEffect(() => {
        if (form.logo && form.logo !== companyRef.current?.logo) {
            handleSubmit();
        }
    }, [form.logo]);

    const getCompanyDetailsHandle = async () => {
        try {
            const res = await getCompanyDetailsApi({ company_id: StorageService.getCompanyId() });
            const d = res?.data?.company;
            setCompanyDetails(d);

            const details = {
                company: d?.company || "",
                contact_no: d?.contact_no || "",
                alt_contact_no: d?.alt_contact_no || "",
                email_id: d?.email_id || "",
                entity_type: d?.entity_type,
                website: d?.website || "",
                gst_no: d?.gst_no || '',
                gst_state: d?.gst_state || {},
                pan_no: d?.pan_no || "",
                fssai_licence: d?.fssai_licence || "",
                address_line1: d?.address_line1 || "",
                address_line2: d?.address_line2 || "",
                state: d?.state,
                city: d?.city,
                pincode: d?.pincode || "",
                company_type: d?.company_type?.map((i) => i?._id) || [],
                logo: d?.logo || "",
            };

            setForm((prev) => {
                const updated = { ...prev, ...details };
                companyRef.current = updated;
                return updated;
            });
        } catch (error) {
            console.error("Company details fetch failed:", error);
        }
    };

    const getStatesByApi = async (countryCode, stateCode) => {
        try {
            const res = await getStatesData({ countryCode, stateCode });
            if (res?.responseCode === 200) {
                // ✅ API response से real state object set करो
                const stateObj = {
                    ...res.data[0],
                    label: res.data[0]?.name,
                    value: res.data[0]?.name,
                };
                setForm((prev) => ({ ...prev, state: stateObj }));
            }
        } catch (error) {
            console.error("State fetch failed:", error);
        }
    };

    const handleCityChange = (data) => {
        setForm((prev) => ({
            ...prev,
            city: {
                label: data?.name,
                value: data?.name,
                _id: data?.name,
            },
            // ✅ Optimistic update — stateName से state तुरंत दिखाओ
            state: {
                label: data?.stateName || "Loading...",
                value: data?.stateName || "",
            },
        }));

        // ✅ Background में API confirm करेगी
        if (data?.countryCode && data?.stateCode) {
            getStatesByApi(data?.countryCode, data?.stateCode);
        }
    };

    const handleOnChange = useCallback(async (name, value) => {
        setForm((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === "gst_no" && value.length === 15) {
                const gstCode = parseInt(value.slice(0, 2), 10);
                updated.gst_state = utils.codeToStateNameObject[gstCode];
            }

            return updated;
        });

        if (formError) {
            const tempState = { ...form, [name]: value };
            const validationResult = await utils.checkFormError(tempState, errorSchema.updateCompanyModalSchema);
            setFormError(validationResult === true ? "" : validationResult);
        }
    }, [form, formError]);

    const callBackTab = (data) => {
        setActiveTab(data);
        const params = new URLSearchParams(location.search);
        params.set('tbn', data);
        navigate(`${location.pathname}?${params.toString()}`);
    };

    const handleCheckboxChange = (e, item) => {
        const { checked } = e.target;
        setForm((prev) => ({
            ...prev,
            company_type: checked
                ? [...prev.company_type, item._id]
                : prev.company_type.filter((type) => type !== item._id),
        }));
    };

    const handleSubmit = async () => {
        const validationResult = await utils.checkFormError(form, errorSchema.updateCompanyModalSchema);
        setFormError(validationResult);

        if (validationResult !== true) return;

        setIsLoading(true);
        try {
            const res = await updateCompanyApi({ ...form });
            if (res?.responseCode === 200) {
                toast.success(res?.responseMessage);
                companyRef.current = { ...form };
                getCompanyDetailsHandle();
            } else {
                toast.error(res?.responseMessage);
                setCroppedImage(null);
            }
        } catch (error) {
            console.error("Update failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const createFile = async (path) => {
        setImageLoading(true);
        try {
            const response = await fetch(path);
            const data = await response.blob();
            const file = new File([data], "test.jpg", { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append("logo", file);

            const res = await dispatch(companyImageUpload(formData));
            if (res?.payload?.responseCode === 200) {
                setForm((prev) => ({ ...prev, logo: res.payload.data.path }));
            }
        } catch (error) {
            console.error("Image upload failed:", error);
        } finally {
            setImageLoading(false);
            setIsModalOpen(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") {
                setOriginalImage(reader.result);
                setIsModalOpen(true);
            }
            e.target.value = null;
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = async (croppedImageUrl) => {
        setCroppedImage(croppedImageUrl);
        await createFile(croppedImageUrl);
    };

    const fetchFromCity = async (search, page, limit) => {
        const res = await getCityLocationList({
            countryCode: "IN",
            stateCode: "",
            offset: page,
            limit,
            search,
        });
        if (res?.responseCode === 200) {
            return {
                // ✅ stateName include — handleCityChange में optimistic use होगा
                items: res.data.all_cities.map((c) => ({
                    ...c,
                    _id: c.name,
                    label: `${c.name}, ${c.stateName}`,
                    stateName: c.stateName,
                })),
                total: res.data.total_record,
            };
        }
        toast.error(res?.responseMessage);
    };

    const profileProps = {
        tabList: constant?.companyProfile_settingTabs,
        callBackTab,
        activeTab,
    };

    return (
        <div className='__profilCmp_main setting-nw-parentBx'>
            <div className='__setsHeads'>
                <h4>{companyDetails?.company}</h4>
                {companyDetails?.company_code && (
                    <span className='__idTag'>ID:{companyDetails?.company_code}</span>
                )}
            </div>

            <div className='__setsContent_prt'>
                <Tab {...profileProps} invoiceBillRaiseClass="settingTbs" />

                {activeTab === 1 && (
                    <>
                        <div className='__stContent'>
                            <div className='__notVerify_bx'>
                                <p>Your company is not verified | Verify your company Detail to get more genuine loads, Bids & much more</p>
                                <button>Verify Now</button>
                            </div>

                            <div className='__cprPr_otr'>
                                <div className='__cprPr_inr'>

                                    {/* Logo */}
                                    <div>
                                        <p className='__cpl_txt'>Company Logo</p>
                                        <div className='__cpr_lg_bx'>
                                            <div>
                                                <input type="file" id='_cpLogo' style={{ display: 'none' }} onChange={handleFileChange} />
                                                <label htmlFor="_cpLogo" className='_cpLogo_upload'>
                                                    {croppedImage || form?.logo ? (
                                                        <img
                                                            className="_cpLogo_upload"
                                                            src={`${masterData?.s3_url}${form?.logo}` || croppedImage}
                                                            alt="Company Logo"
                                                        />
                                                    ) : (
                                                        <LuUpload />
                                                    )}
                                                </label>
                                            </div>
                                            <p>Preferred Image Dimensions: 240 x 240 pixels @ 72 DPI Maximum File Size: 1MB</p>
                                        </div>
                                    </div>

                                    {/* Basic Info */}
                                    <div>
                                        <div className='__cprPr_fxOtr'>
                                            <div className='__cprPr_fxInr'>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="Business Name" placeholder="Business Name"
                                                        value={form?.company || ""} name="company" maxLength={40}
                                                        focus={!!formError?.company} error={formError.company}
                                                        onChange={({ target: { name, value } }) => handleOnChange(name, value)}
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    <CustomSelect
                                                        label="Entity Type" options={entityList}
                                                        onSelect={(data) => handleOnChange("entity_type", data?.value)}
                                                        placeholder="Select Entity" error={formError["entity_type"]}
                                                        value={entityList?.find((i) => i.value == form?.entity_type)}
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="Contact Number" placeholder="Contact Number"
                                                        value={form?.contact_no || ""} maxLength={10} disabled name="contact_no"
                                                        focus={!!formError?.contact_no} error={formError.contact_no}
                                                        onChange={({ target: { name, value } }) => /^\d*$/.test(value) && handleOnChange(name, value)}
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="Alternate Contact Number" placeholder="Alternate Contact Number"
                                                        value={form?.alt_contact_no || ""} maxLength={10} name="alt_contact_no"
                                                        focus={!!formError?.alt_contact_no} error={formError.alt_contact_no}
                                                        onChange={({ target: { name, value } }) => {
                                                            let cleaned = value.replace(/\D/g, '');
                                                            if (cleaned.startsWith('91') && cleaned.length > 10) cleaned = cleaned.substring(2);
                                                            if (/^\d{0,10}$/.test(cleaned)) handleOnChange(name, cleaned);
                                                        }}
                                                    />
                                                </div>
                                                <CommonInput
                                                    type="text" label="Email ID" placeholder="Email ID"
                                                    value={form?.email_id || ""} name="email_id" maxLength={100}
                                                    focus={!!formError?.email_id} error={formError.email_id}
                                                    onChange={({ target: { name, value } }) => handleOnChange(name, value)}
                                                />
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="Website" placeholder="Website"
                                                        value={form?.website || ""} name="website" maxLength={30}
                                                        focus={!!formError?.website} error={formError.website}
                                                        onChange={({ target: { name, value } }) => handleOnChange(name, value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* GST */}
                                    <div>
                                        <p className='__cp_head'>GST Registration</p>
                                        <div className='__cprPr_fxOtr'>
                                            <div className='__cprPr_fxInr'>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="GST Number" placeholder="GST Number"
                                                        value={form?.gst_no || ""} name="gst_no" maxLength={15}
                                                        focus={!!formError?.gst_no} error={formError.gst_no}
                                                        onChange={({ target: { name, value } }) =>
                                                            handleOnChange(name, value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())
                                                        }
                                                    />
                                                </div>
                                                {form?.gst_no && (
                                                    <div className='__cp_flex_half'>
                                                        <CustomSelect
                                                            options={filteredCities} placeholder="Select a state"
                                                            value={form?.gst_state} disabled
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className='__cprPr_fxInr'>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="PAN Number" placeholder="PAN Number" name='pan_no'
                                                        value={form?.pan_no || ""} maxLength={10}
                                                        focus={!!formError?.pan_no} error={formError.pan_no}
                                                        onChange={({ target: { name, value } }) =>
                                                            handleOnChange(name, value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())
                                                        }
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="Fssai License Number" placeholder="Fssai License Number" name='fssai_licence'
                                                        value={form?.fssai_licence || ""} maxLength={14}
                                                        focus={!!formError?.fssai_licence} error={formError.fssai_licence}
                                                        onChange={({ target: { name, value } }) =>
                                                            handleOnChange(name, value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div>
                                        <p className='__cp_head'>Business Address</p>
                                        <div className='__cprPr_fxOtr'>
                                            <div className='__cprPr_fxInr'>
                                                <div className='__cp_flex_full'>
                                                    <CommonInput
                                                        type="text" label="Address" placeholder="Address" name='address_line1'
                                                        value={form?.address_line1 || ""} maxLength={255}
                                                        focus={!!formError?.address_line1} error={formError.address_line1}
                                                        onChange={({ target: { name, value } }) => handleOnChange(name, value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className='__cprPr_fxInr'>
                                                <div className='__cp_flex_half'>
                                                    <CityDropdownForTrip
                                                        label="City" placeholder="Select a city"
                                                        fetchData={fetchFromCity}
                                                        selectedItem={form?.city}
                                                        onChange={handleCityChange}
                                                        error={formError["city.label"]}
                                                        errorMessage={formError["city.label"]}
                                                        pageSize={20}
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    {/* ✅ form.state — optimistic + API confirmed दोनों यहाँ दिखेंगे */}
                                                    <CustomSelect
                                                        options={filteredCities} placeholder="Select a state"
                                                        value={form?.state} disabled
                                                    />
                                                </div>
                                                <div className='__cp_flex_half'>
                                                    <CommonInput
                                                        type="text" label="PIN Code" placeholder="PIN Code"
                                                        value={form?.pincode || ""} name="pincode" maxLength={6}
                                                        focus={!!formError?.pincode} error={formError.pincode}
                                                        onChange={({ target: { name, value } }) => /^\d*$/.test(value) && handleOnChange(name, value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Transport Type */}
                                    <div>
                                        <p className='__cp_head'>Transport Type</p>
                                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                            {masterData?.company_type?.map((item, index) => (
                                                <Checkbox
                                                    key={item._id} name={item.name} id={`company_type_${index}`}
                                                    className="__trkChecks"
                                                    handleClick={(e) => handleCheckboxChange(e, item)}
                                                    isChecked={form.company_type?.includes(item._id)}
                                                    label={item.name}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className='__setFooter'>
                            <button
                                className='__setBtn __setBtn_update'
                                disabled={isEqal || isLoading}
                                onClick={handleSubmit}
                            >
                                {isLoading ? "Processing..." : "Update"}
                            </button>
                            <button className='__setBtn __setBtn_cancel' onClick={getCompanyDetailsHandle}>
                                Cancel
                            </button>
                        </div>
                    </>
                )}

                {activeTab === 2 && <CompanySetting setActiveTab={setActiveTab} />}
                {activeTab === 3 && <InvoiceFormat setActiveTab={setActiveTab} />}

                <ImageCropModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onCrop={handleCropComplete}
                    imageSrc={originalImage}
                    imageLoading={imageLoading}
                />
            </div>
        </div>
    );
};

export default CompanyProfile;